<template>
  <div class="rating-input-container mb-3">
    <label>
      <question-display :question="question"></question-display>
    </label>

    <div v-for="option in ratingOptions" :key="option.value" class="rating-row">
      <!-- attribute name full width on its own line -->
      <label class="star-label">
        {{ option.name }}
        <span v-if="isNumericRating(ratings[option.value])">
          (<small :class="getStatusClass(ratings[option.value])">{{ getStatusLabel(ratings[option.value]) }}</small>)
        </span>
      </label>

      <!-- stars left, Did Not Use button far right — same line -->
      <div class="rating-controls">
        <div class="star-rating" :class="{ faded: isSpecialValue(ratings[option.value]) }">
          <span
            v-for="star in 5"
            :key="star"
            :data-option="option.value"
            @click="rate(option.value, star)"
            :class="['star', { filled: ratings[option.value] === star || ratings[option.value] > star }]"
          >★</span>
        </div>

        <button
          type="button"
          class="special-btn"
          :class="{ active: ratings[option.value] === 'DNU' }"
          @click="setSpecial(option.value, 'DNU')"
        >Don't Know</button>
      </div>
    </div>

    <small
      v-if="showValidation && question.required && hasUnratedOptions"
      class="text-danger"
    >
      All ratings are required.
    </small>
  </div>
</template>

<script>
import surveyService from '@/services/surveyService';
import QuestionDisplay from '../utils/QuestionDisplay.vue';

export default {
  components: { QuestionDisplay },
  props: {
    question: Object,
    required: Boolean,
    showValidation: Boolean
  },
  data() {
    return {
      ratings: surveyService.getAnswer(this.question.id) || this.initializeRatings()
    };
  },
  computed: {
    ratingOptions() {
      return this.question.options.filter(o => o.name.toLowerCase() !== 'did not use');
    },
    hasUnratedOptions() {
      return this.ratingOptions.some(o => this.ratings[o.value] === 0 || this.ratings[o.value] === undefined);
    }
  },
  methods: {
    initializeRatings() {
      const init = {};
      this.question.options.forEach(o => { init[o.value] = 0; });
      return init;
    },
    rate(optionValue, stars) {
      this.ratings = { ...this.ratings, [optionValue]: stars };
      surveyService.setAnswer(this.question.id, this.ratings);
      this.$emit('input', this.ratings);

      const starEls = this.$el.querySelectorAll(`[data-option="${optionValue}"]`);
      starEls.forEach(el => {
        el.classList.remove('animate');
        void el.offsetWidth;
        el.classList.add('animate');
        setTimeout(() => el.classList.remove('animate'), 200);
      });
    },
    setSpecial(optionValue, val) {
      const current = this.ratings[optionValue];
      // Toggle off if already active
      const newVal = current === val ? 0 : val;
      this.ratings = { ...this.ratings, [optionValue]: newVal };
      surveyService.setAnswer(this.question.id, this.ratings);
      this.$emit('input', this.ratings);
    },
    isSpecialValue(val) {
      return val === 'DNU' || val === 'DK';
    },
    isNumericRating(val) {
      return typeof val === 'number' && val > 0;
    },
    getStatusLabel(value) {
      return { 1: 'Poor', 2: 'Below Average', 3: 'Average', 4: 'Good', 5: 'Excellent' }[value] || '';
    },
    getStatusClass(value) {
      return { 1: 'text-danger', 2: 'text-warning', 3: 'text-warning', 4: 'text-primary', 5: 'text-success' }[value] || '';
    }
  },
  watch: {
    question(newQ) {
      this.ratings = surveyService.getAnswer(newQ.id) || this.initializeRatings();
    }
  }
};
</script>

<style scoped>
/* Each attribute block: label full width, then stars+button row below */
.rating-row {
  display: flex;
  flex-direction: column;
  padding: 8px 0;
}

/* Selectors below are intentionally scoped to .rating-row/.rating-controls to raise
   specificity above the stale global rules in assets/css/style.css
   (.rating-input-container .star-label and .star-rating, both width:50% !important).
   Without the extra class the .star-label override would only tie on specificity and
   be decided by stylesheet injection order. */
.rating-row .star-label {
  font-weight: 500;
  margin-bottom: 4px;
  /* override global .rating-input-container .star-label { width:50% !important } */
  width: 100% !important;
}

/* stars on left, Don't Know button right next to stars */
.rating-controls {
  display: flex;
  align-items: center;
  gap: 12px;
}

.rating-controls .star-rating {
  display: inline-flex;
  align-items: center;
  /* override global .star-rating { width:50% !important } that otherwise forces the
     star block to half width and pushes the Don't Know button to the right */
  width: auto !important;
}

.star-rating.faded .star {
  opacity: 0.35;
}

.star-rating .star {
  font-size: 3rem;
  color: #ccc;
  cursor: pointer;
  transition: transform 0.2s ease, color 0.2s ease;
}

.star-rating .star.filled {
  color: gold;
}

.star-rating .star.animate {
  transform: scale(1.3);
}

.special-btn {
  padding: 8px 20px;
  font-size: 1rem;
  border: 1px solid #555;
  border-radius: 4px;
  background: #d0d0d0;
  color: #000;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.2s, color 0.2s, border-color 0.2s;
}

.special-btn:hover {
  background: #c0c0c0;
}

.special-btn.active {
  background: #4a90d9;
  color: #fff;
  border-color: #3a7bbf;
}
</style>
